import React, { useRef, useState, useEffect, useLayoutEffect, useImperativeHandle } from 'react';
import { isFirefox } from 'react-device-detect';
import MoreHorizontalIcon from '@nokia-csf-uxr/ccfk-assets/MoreHorizontalIcon';
import HorizontalList, { HorizontalListProps } from 'components/HorizontalList';
import IconButton from 'components/IconButton';
import { getContext, useCombinedRefs, isAnimationDisabled } from 'common';
import { CONSTANTS } from 'common/ListKeyboardNav';
import Menu, { MenuRef, MenuProps } from 'components/Menu';
import KEY_CODES from 'common/KeyCodes';
import { getMenuMaxWidth } from 'common/MenuDimensionCalc';
import { zIndex } from 'styles';
import { animated, useTransition, useSpring } from 'react-spring';
import useMeasure from 'react-use-measure';
import { TRANSITION_ACCELERATE } from '@nokia-csf-uxr/freeform-design-tokens/tokens/transition';
import { SPEED_SLOW } from '@nokia-csf-uxr/freeform-design-tokens/tokens/speed';
import { getEasing, getDuration } from 'common/parse-tokens';
import styled from './styles';

const {
  TAB, ESCAPE, ARROW_RIGHT, ARROW_LEFT, ENTER_KEY, SPACE_KEY
} = KEY_CODES;

const { LIST_ITEM_TABABLE_ITEM } = CONSTANTS;

const getDefaultMenuPositioning = (RTL, offset) => ({
  placement: RTL ? 'right' : 'left',
  modifiers: [
    {
      name: 'offset',
      options: {
        offset: [0, parseInt(`-${offset}`, 10)]
      }
    }
  ]
});

const NON_ANIMATED_LIST_ITEM_SIZE = 2.75;
const NON_ANIMATED_FIRST_AND_LAST_ITEM_SIZE = 2.375;

export interface HorizontalMoreButtonProps extends React.ComponentPropsWithoutRef<'button'> {
  /** To render the menu items, consider using 'HorizontalMoreButtonItem'. */
  children: React.ReactNode;
  /** Control `menu` open state. */
  isOpen?: boolean;
  /** Override `Menu` props used for positioning actions menu with respect to icon button. */
  menuProps?: Partial<MenuProps>;
  /** Override `List` props that act as a container for the menu items inside menu. */
  listProps?: Partial<HorizontalListProps>;
  /** Override `IconButton` content. */
  buttonContent?: React.ReactNode;
  /** Any element ref that triggers the menu. Use when the trigger element is not the default IconButton */
  triggerEl?: React.RefObject<HTMLElement>;
  /**
   * Callback fired when the component requests to be opened.
   *
   * @param {object} event The event source of the callback.
   */
  onOpen?: (arg: React.MouseEvent | React.KeyboardEvent) => void;
  /**
   * Callback fired when the component requests to be closed.
   *
   * @param {object} event The event source of the callback.
   */
  onClose?: (arg: React.MouseEvent | React.KeyboardEvent) => void;
  /**
   * Callback fired when the component animation ends.
   *
   * @param {object} event The event source of the callback.
   */
  onListAnimationEnd?: () => void;
  /**
   * If true, the last Icon in menu list is same as menu icon which trigger the menu to expand.
   * The horizontal menu animation is different based on the value it.
   */
  isLastIconSameAsMenuIcon?: boolean;
  /**
   * If internalTriggerBtnAnimation and menuAnimation properties are empty object, animation on horizontal menu is disabled.
   * This uses React-spring transitions config. See https://react-spring.io for more information.
   */
  animationProps?: {
    internalTriggerBtnAnimation?: any,
    menuAnimation?: any
  };
  /** If true, disable all motion animation for this component.  This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
  /** @ignore */
  onMouseOver?: React.MouseEventHandler;
  /** @ignore */
  onFocus?: React.FocusEventHandler;
  /** @ignore */
  onKeyDown?: React.KeyboardEventHandler;
}

export type HorizontalMoreButtonHandle = {
  triggerRef?: React.RefObject<HTMLElement>,
  menuRef?: React.RefObject<MenuRef>,
  positionMenu: (any) => void
};


/** Summons an overflow menu with just icons inline.
 * Any other props than listed will be provided to the root `button` element.
 * To re-position menu on changes to children or target element, please use `positionMenu` callback using component ref.
 * The forwarded ref has 'triggerRef' and 'menuRef'.
 */
const HorizontalMoreButton = React.forwardRef<HorizontalMoreButtonHandle, HorizontalMoreButtonProps>((props: HorizontalMoreButtonProps, ref) =>
  getContext(({ theme: { ELEVATIONS: elevationTheme }, RTL, disableAnimation }) => {
    const {
      children,
      isOpen,
      menuProps,
      listProps,
      buttonContent,
      triggerEl,
      onOpen,
      onClose,
      onMouseOver,
      onFocus,
      onKeyDown,
      isLastIconSameAsMenuIcon,
      animationProps,
      disableAnimation: propsdisableAnimation,
      onListAnimationEnd,
      ...buttonProps
    } = props;
    const {
      positioningOptions,
      onMouseOut: onMenuMouseOut,
      onBlur: onMenuBlur,
      style: menuStyle,
      ...otherMenuProps
    } = menuProps;
    const {
      ref: passedListRef,
      style,
      onMouseDown: onListMouseDown,
      onKeyDown: onListKeyDown,
      onClick: onListClick,
      elevationProps = {},
      onTransitionEnd: onListTransitionEnd,
      ...otherListProps
    } = listProps;
    const referenceElement = useRef<HTMLElement>(null);
    const listRef = useRef(null);
    const elevationRef = useRef(null);
    const positionOffsetTimeoutRef = useRef<any>();
    const combinedListRef = useCombinedRefs(passedListRef, listRef);
    const [menuRef, setMenuRef] = useState(null);
    const [listMaxWidth, setListMaxWidth] = useState(null);
    const [refMaxWidth, setRefMaxWidth] = useState(null);
    const [positionOffset, setPositionOffset] = useState(RTL ? { left: '0' } : { right: '0' });
    const isMenuTriggeredByInternalBtn = !(triggerEl.current);

    const resolvedDisableAnimation = isAnimationDisabled(propsdisableAnimation, disableAnimation);
    const resolvedPositioning = {
      ...getDefaultMenuPositioning(RTL, refMaxWidth),
      ...positioningOptions
    };
    const { darkBackgroundColor } = elevationTheme[0];

    const isMouseOutsideMenu = event => menuRef && menuRef.wrapperRef && !menuRef.wrapperRef.contains(event.relatedTarget);

    const toggleMenu = (event) => {
      if (isOpen) {
        onClose && onClose(event);
      } else {
        onOpen && onOpen(event);
      }
    };

    const focusTriggerEl = () => {
      referenceElement.current && referenceElement.current.focus();
    };

    const handleButtonMouseOver = (event) => {
      onOpen && onOpen(event);
      onMouseOver && onMouseOver(event);
    };

    const handleButtonKeyDown = (event) => {
      switch (event.key) {
        case ARROW_RIGHT:
          if (RTL) {
            event.preventDefault();
            onOpen && onOpen(event);
          }
          break;
        case ARROW_LEFT:
          if (!RTL) {
            event.preventDefault();
            onOpen && onOpen(event);
          }
          break;
        case ENTER_KEY:
        case SPACE_KEY:
          event.preventDefault();
          event.stopPropagation();
          toggleMenu(event);
          break;
        case ESCAPE:
          onClose && onClose(event);
          break;
        default:
          break;
      }
      onKeyDown && onKeyDown(event);
    };

    const handleListFocus = (event) => {
      // get all list items
      const elements = event.target.querySelectorAll(`.${LIST_ITEM_TABABLE_ITEM}`);
      const trigger = isMenuTriggeredByInternalBtn ? referenceElement.current : triggerEl.current;
      const triggerElLeftPos = trigger.getBoundingClientRect().left;
      const listLeftPos = listRef.current.getBoundingClientRect().left;
      if (listLeftPos < triggerElLeftPos) {
        // focus on last list item
        const last = elements[elements.length - 1];
        last && last.focus();
      } else {
        // focus on first list item
        const first = elements[0];
        first && first.focus();
      }
    };

    const handleListMouseDown = (event) => {
      event.preventDefault();
      focusTriggerEl();
      onListMouseDown && onListMouseDown(event);
    };

    const handleListClick = (event) => {
      onClose && onClose(event);
      onListClick && onListClick(event);
    };

    const handleListKeyDown = (event) => {
      switch (event.key) {
        case ESCAPE:
        case ENTER_KEY:
        case SPACE_KEY:
          event.preventDefault();
          onClose && onClose(event);
          focusTriggerEl();
          break;
        case TAB:
          onClose && onClose(event);
          event.preventDefault();
          focusTriggerEl();
          break;
        default:
          break;
      }
      onListKeyDown && onListKeyDown(event);
    };

    const handleMenuMouseOut = (event) => {
      if (isMouseOutsideMenu(event)) {
        onClose && onClose(event);
      }
    };

    // Hook to make available the positionMenu function of the Menu component to the user.
    // User would call the moreButtonRef.positionMenu()
    useImperativeHandle(
      ref,
      () => ({
        triggerRef: isMenuTriggeredByInternalBtn ? referenceElement : triggerEl,
        menuRef,
        positionMenu: () => {
          menuRef && menuRef.positionMenu();
        }
      }),
      [menuRef]
    );

    useLayoutEffect(() => {
      if (!isOpen) return;
      menuRef && menuRef.positionMenu();
    }, [isOpen, menuProps, listProps]);

    useEffect(() => {
      setListMaxWidth(getMenuMaxWidth(referenceElement.current));
      referenceElement.current && setRefMaxWidth(referenceElement.current.getBoundingClientRect().width);
    }, [referenceElement.current]);

    useEffect(() => {
      // There are some cases where the trigger element is not rendered full and hence the dimensions are 0.
      // Delay getting the dimensions in these cases.
      triggerEl.current &&
        setTimeout(() => {
          const triggerElWidth = triggerEl.current.getBoundingClientRect().width;
          triggerElWidth > 0 && setRefMaxWidth(triggerElWidth);
          triggerElWidth > 0 && setListMaxWidth(getMenuMaxWidth(triggerEl.current));
        });
    }, [triggerEl.current]);

    const btnSpring = useSpring(animationProps.internalTriggerBtnAnimation || {
      opacity: isOpen ? 0 : 1,
      config: {
        duration: getDuration(SPEED_SLOW),
        easing: getEasing(TRANSITION_ACCELERATE)
      },
      immediate: resolvedDisableAnimation
    });

    const [referenceDivRef, listBound] = useMeasure();
    const transitionInit = useRef(false);
    const isOpenRef = useRef(isOpen);
    useEffect(() => {
      if (isOpenRef.current !== isOpen && !transitionInit.current) {
        transitionInit.current = true;
      }
    });

    // Because list has 0.125rem side padding.
    // For making the last icon in list will sit at the same position of the trigger icon,
    // the list need to be moved 0.125rem horizontally.
    useLayoutEffect(() => {
      if (isOpenRef.current !== isOpen) {
        positionOffsetTimeoutRef.current && clearTimeout(positionOffsetTimeoutRef.current);
        if (isOpen === false) {
          positionOffsetTimeoutRef.current = setTimeout(
            () => setPositionOffset(RTL ? { left: '0' } : { right: '0' }),
            getDuration(SPEED_SLOW)
          );
        }
        if (isOpen === true) {
          const offset = '-0.125rem';
          setPositionOffset(RTL ? { left: offset } : { right: offset });
          positionOffsetTimeoutRef.current = setTimeout(() => combinedListRef.current && combinedListRef.current.focus(), getDuration(SPEED_SLOW));
        }
      }
      isOpenRef.current = isOpen;
    }, [isOpen]);

    const transitions = useTransition(
      isOpen,
      null,
      animationProps.menuAnimation || {
        from: { maxWidth: isLastIconSameAsMenuIcon ? '2rem' : '0rem' },
        enter: { maxWidth: `${listBound.width / 16}rem` },
        leave: { maxWidth: isLastIconSameAsMenuIcon ? '2rem' : '0rem' },
        unique: true,
        config: {
          duration: getDuration(SPEED_SLOW),
          easing: getEasing(TRANSITION_ACCELERATE)
        },
        onStart: () => {
          if (!transitionInit.current) {
            return;
          }
          const target = isMenuTriggeredByInternalBtn ? referenceElement.current : triggerEl.current;
          if (target && isLastIconSameAsMenuIcon) {
            target.style.backgroundColor = darkBackgroundColor;
            target.style.zIndex = `${zIndex.MENU + 1}`;
            // this is for prevent: unstable_flushDiscreteUpdates: Cannot flush updates when React is already rendering warning.
            setTimeout(() => !isOpen && target.focus());
          }
        },
        onRest: () => {
          const target = isMenuTriggeredByInternalBtn ? referenceElement.current : triggerEl.current;
          if (target && isLastIconSameAsMenuIcon) {
            target.style.backgroundColor = 'transparent';
            target.style.zIndex = '1';
          }
          onListAnimationEnd && onListAnimationEnd();
        },
        immediate: resolvedDisableAnimation
      }
    );

    const internalTriggerBtn = () => (
      <IconButton
        ref={referenceElement}
        aria-expanded={isOpen}
        onMouseOver={e => handleButtonMouseOver(e)}
        onFocus={e => onFocus && onFocus(e)}
        onKeyDown={e => handleButtonKeyDown(e)}
        {...buttonProps}
      >
        {!!buttonContent && buttonContent}
        {!buttonContent && <MoreHorizontalIcon />}
      </IconButton>
    );

    const internalTriggerBtnAnimation1 = () =>
      !isLastIconSameAsMenuIcon ? !resolvedDisableAnimation ? (
        <animated.div style={btnSpring}>{internalTriggerBtn()}</animated.div>
      ) : (
        internalTriggerBtn()
      )
        : (
          internalTriggerBtn()
        );

    if (!(resolvedDisableAnimation && isFirefox)) {
      return (
        <>
          <styled.ReferenceDiv ref={referenceDivRef} listMaxWidth={listMaxWidth} />
          {isMenuTriggeredByInternalBtn && internalTriggerBtnAnimation1()}
          {/* Ensure triggerEl is not undefined when Menu renders */}
          {(referenceElement.current || triggerEl.current) &&
          transitions.map(({ item, key, props: transitionStyles }) =>
            item && (
            <Menu
              key={key}
              as={!resolvedDisableAnimation && animated.div}
              keepMounted
              ref={setMenuRef}
              isOpen={isOpen}
              triggerEl={isMenuTriggeredByInternalBtn ? referenceElement : triggerEl}
              usePortal
              positioningOptions={resolvedPositioning}
              onClose={onClose}
              onMouseOut={(e) => {
                handleMenuMouseOut(e);
                onMenuMouseOut && onMenuMouseOut(e);
              }}
              onBlur={(e) => {
                handleMenuMouseOut(e);
                onMenuBlur && onMenuBlur(e);
              }}
              style={{
                maxHeight: '2.25rem',
                overflow: 'hidden',
                maxWidth: listMaxWidth,
                ...menuStyle,
                ...transitionStyles,
                ...positionOffset
              }}
              {...otherMenuProps}
            >
              <HorizontalList
                role="menu"
                ref={combinedListRef}
                style={{
                  maxWidth: listMaxWidth,
                  padding: '0.125rem'
                }}
                onFocus={handleListFocus}
                onMouseDown={handleListMouseDown}
                onClick={handleListClick}
                onKeyDown={handleListKeyDown}
                elevationProps={{
                  ref: elevationRef,
                  ...elevationProps
                }}
                {...otherListProps}
              >
                {children}
              </HorizontalList>
            </Menu>
            ))}
        </>
      );
    }
    // No animation and Firefox. Firefox has issues with this animation when immediate = true
    const numberOfChildren = React.Children.count(children);
    const listWidth = ((numberOfChildren - 2) * NON_ANIMATED_LIST_ITEM_SIZE) + (2 * NON_ANIMATED_FIRST_AND_LAST_ITEM_SIZE) + 0.25 + 0.0125; // adding padding
    return (
      <>
        <styled.ReferenceDiv ref={referenceDivRef} listMaxWidth={listMaxWidth} />
        {isMenuTriggeredByInternalBtn && internalTriggerBtnAnimation1()}
        {/* Ensure triggerEl is not undefined when Menu renders */}
        {((referenceElement.current || triggerEl.current) && isOpen) &&
        <Menu
          keepMounted
          ref={setMenuRef}
          isOpen={isOpen}
          triggerEl={isMenuTriggeredByInternalBtn ? referenceElement : triggerEl}
          usePortal
          positioningOptions={resolvedPositioning}
          onClose={onClose}
          onMouseOut={(e) => {
            handleMenuMouseOut(e);
            onMenuMouseOut && onMenuMouseOut(e);
          }}
          onBlur={(e) => {
            handleMenuMouseOut(e);
            onMenuBlur && onMenuBlur(e);
          }}
          style={{
            maxHeight: '2.25rem',
            overflow: 'hidden',
            maxWidth: listMaxWidth,
            width: `${listWidth}rem`,
            ...menuStyle,
            ...positionOffset
          }}
          {...otherMenuProps}
        >
          <HorizontalList
            role="menu"
            ref={combinedListRef}
            style={{
              maxWidth: listMaxWidth,
              padding: '0.125rem'
            }}
            onFocus={handleListFocus}
            onMouseDown={handleListMouseDown}
            onClick={handleListClick}
            onKeyDown={handleListKeyDown}
            elevationProps={{
              ref: elevationRef,
              ...elevationProps
            }}
            {...otherListProps}
          >
            {children}
          </HorizontalList>
        </Menu>
      }
      </>
    );
  }));

HorizontalMoreButton.displayName = 'HorizontalMoreButton';

HorizontalMoreButton.defaultProps = {
  isOpen: false,
  menuProps: {},
  listProps: {},
  buttonContent: undefined,
  triggerEl: {
    current: undefined
  },
  onOpen: undefined,
  onClose: undefined,
  isLastIconSameAsMenuIcon: false,
  animationProps: {},
  disableAnimation: undefined,
  onListAnimationEnd: undefined,
  onMouseOver: undefined,
  onKeyDown: undefined,
  onFocus: undefined
};

export default HorizontalMoreButton;
